import React, { useMemo } from "react";
import { TRIAL_LENGTH_DAYS } from "@common/flatRateBilling";
import { ENTERPRISE_TRIAL_CALL_URL } from "@common/billing";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useOptionalSupportChat } from "@components/supportChat/useSupportChat";
import { Faq, Question } from "@design-system/FAQ/Faq";
import { BookCallButton } from "@design-system/BookCall/BookCallButton";
import { Section } from "@design-system/Section/Section";
import { useSettings } from "../SettingsProvider";
import styles from "./FAQSection.module.scss";
import { Button } from "@design-system/Button/Button";

interface Props {
    onCallBooked: () => void;
}

export const FAQSection: React.FC<Props> = ({ onCallBooked }) => {
    const { isFetched } = useBillingStatus();
    const { open: openSettings } = useSettings();
    const supportChat = useOptionalSupportChat();
    const { billingCatalogV3 } = useTypedFlags();

    const questions: Question[] = useMemo(() => {
        if (!isFetched) {
            return [];
        }

        return [
            {
                question: `What’s included in my ${TRIAL_LENGTH_DAYS}-day free trial?`,
                answer: `When you start a ${TRIAL_LENGTH_DAYS}-day Free Trial on the Basic or Plus Plan, you get access to all paid features and the last 4 months of transaction categorization to preview your Profit and Loss and other reports.`,
            },

            {
                question: "How does Kick’s pricing work?",
                answer: (
                    <>
                        <p>
                            Kick plans are tailored to support Business Owners
                            at every stage:{" "}
                        </p>
                        <ul className={styles.unorderedList}>
                            <li>
                                {billingCatalogV3
                                    ? "0 - 250 transactions"
                                    : "$0 - $25k annual business expenses"}
                                : Kick is completely free.
                            </li>
                            <li>
                                {billingCatalogV3
                                    ? "250+ transactions"
                                    : "$25k+ annual business expenses"}
                                : Enjoy a 14-day trial of our Basic or Plus
                                plan. Cancel anytime during your trial. After
                                your trial ends, you’ll be charged annually for
                                Basic or quarterly for Plus.
                            </li>
                            <li>
                                <b>Want fully self-driving books?</b> We’ll
                                build you a custom plan with onboarding,
                                dedicated support, reconciled books, and annual
                                close.{" "}
                                <BookCallButton
                                    variant="link"
                                    size="sm"
                                    url={ENTERPRISE_TRIAL_CALL_URL}
                                    onBooked={onCallBooked}
                                    className={styles.inlineButton}
                                >
                                    Book a call
                                </BookCallButton>{" "}
                                and we’ll design the right setup for your
                                business.
                            </li>
                        </ul>
                    </>
                ),
            },

            {
                question: "Does Kick offer tax services?",
                answer: (
                    <>
                        Kick focuses on automating the bookkeeping and the tax
                        prep workflows for Business Owners and their
                        Accountants. We recommend partnering with a local
                        Accountant. Reach out to{" "}
                        <a href="mailto:sales@kick.co">sales@kick.co</a> for a
                        list of recommended tax professionals.
                    </>
                ),
            },
            {
                question: "How do I pause or cancel my subscription?",
                answer: (
                    <>
                        To cancel your subscription go to{" "}
                        <Button
                            variant="link"
                            size="sm"
                            className={styles.inlineButton}
                            onClick={() => openSettings(["billing"])}
                        >
                            Settings {">"} Billing
                        </Button>
                        {". "}
                        After canceling you will continue to have access to data
                        during your Subscription period.
                    </>
                ),
            },
            {
                question: "Can I delete my Kick account?",
                answer: (
                    <>
                        You can update or pause your subscription at any time in
                        your Billing settings. To delete your account and erase
                        your data, click the support chat{" "}
                        {supportChat ? (
                            <Button
                                variant="link"
                                size="sm"
                                className={styles.inlineButton}
                                onClick={supportChat.openSupportChat}
                            >
                                button
                            </Button>
                        ) : (
                            "button"
                        )}{" "}
                        in the bottom-left corner of the app.
                    </>
                ),
            },
        ];
    }, [isFetched, onCallBooked, openSettings, supportChat, billingCatalogV3]);

    return (
        <Section heading="FAQ">
            <Faq questions={questions} />
        </Section>
    );
};
